/**
 * ISO 8601 week math — shared by the follow-up and snooze tagging systems.
 *
 * Both systems encode per-week state as `<prefix>-{YYYY-W##}` tags on a deal's
 * `tags` array (e.g. `followed-up-2026-W15`, `snoozed-2026-W15`). Keeping the
 * week computation in one module guarantees the two stay in lockstep — a follow-up
 * tag and a snooze tag for the same calendar week always resolve to the same
 * `YYYY-W##` string.
 */

/** One week in milliseconds. */
const WEEK_MS = 604800000;

/** One day in milliseconds. */
const DAY_MS = 86400000;

/**
 * Hard cap on how many weeks a single range may span. Guards against an absurd
 * date range (or a from/to swap that wasn't normalized) producing thousands of
 * tags. ~2 years is well beyond any realistic snooze window.
 */
export const MAX_WEEKS_IN_RANGE = 105;

/**
 * Get the ISO week number for a given date.
 * Returns format: "YYYY-W##" (e.g., "2026-W15").
 *
 * ISO 8601 definition: Week 1 is the week with the first Thursday of the year.
 * Weeks start on Monday and end on Sunday.
 *
 * This matches PostgreSQL's IYYY and IW format specifiers.
 * Uses UTC to match database timezone.
 */
export function getISOWeekString(date: Date = new Date()): string {
  // Work in UTC to match database
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();

  // Create UTC date
  const target = new Date(Date.UTC(year, month, day));

  // Get day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const dayOfWeek = target.getUTCDay();

  // ISO weeks start on Monday, so adjust (Monday = 1, Sunday = 7)
  const isoDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;

  // Find the Thursday of this week (used to determine week number)
  const thursdayOfWeek = new Date(target);
  thursdayOfWeek.setUTCDate(day + (4 - isoDayOfWeek));

  // The ISO year is the year of the Thursday
  const isoYear = thursdayOfWeek.getUTCFullYear();

  // Find the first Thursday of the ISO year (this is in week 1)
  const jan4 = new Date(Date.UTC(isoYear, 0, 4));
  const jan4DayOfWeek = jan4.getUTCDay() === 0 ? 7 : jan4.getUTCDay();
  const firstThursday = new Date(jan4);
  firstThursday.setUTCDate(4 + (4 - jan4DayOfWeek));

  // Calculate week number
  const weekNum = 1 + Math.floor((thursdayOfWeek.getTime() - firstThursday.getTime()) / WEEK_MS);

  return `${isoYear}-W${String(weekNum).padStart(2, "0")}`;
}

/**
 * Get every distinct ISO week string a calendar range touches, inclusive of both
 * ends, in chronological order.
 *
 * The range is normalized (from/to may be passed in either order) and truncated
 * to date precision in UTC, so partial days do not produce phantom weeks. The
 * result is capped at {@link MAX_WEEKS_IN_RANGE} weeks.
 *
 * Example: a range from Mon 2026-06-08 to Fri 2026-06-19 returns
 * `["2026-W24", "2026-W25"]`.
 */
export function getWeekStringsInRange(from: Date, to: Date): string[] {
  const startMs = Math.min(toUtcMidnight(from), toUtcMidnight(to));
  const endMs = Math.max(toUtcMidnight(from), toUtcMidnight(to));

  const weeks: string[] = [];
  const seen = new Set<string>();

  // Step day-by-day so week boundaries are never skipped regardless of which
  // weekday the range starts on. Dedup into a Set; preserve chronological order.
  for (let ms = startMs; ms <= endMs; ms += DAY_MS) {
    const week = getISOWeekString(new Date(ms));
    if (!seen.has(week)) {
      seen.add(week);
      weeks.push(week);
      if (weeks.length >= MAX_WEEKS_IN_RANGE) break;
    }
  }

  return weeks;
}

/** Truncate a date to UTC midnight (ms since epoch). */
function toUtcMidnight(date: Date): number {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/**
 * Inverse of {@link getISOWeekString}: returns the Monday (UTC) that starts a
 * given `YYYY-W##` ISO week, or `null` if the string is malformed.
 *
 * Used for human-readable display of a stored week tag (e.g. rendering
 * "Mon Jul 20" for the snooze/scheduled-follow-up week).
 */
export function isoWeekToMondayUtc(week: string): Date | null {
  const match = /^(\d{4})-W(\d{2})$/.exec(week);
  if (!match) return null;
  const isoYear = Number(match[1]);
  const weekNum = Number(match[2]);
  if (weekNum < 1 || weekNum > 53) return null;

  // Jan 4th is always in ISO week 1. Find that week's Monday, then add weeks.
  const jan4 = new Date(Date.UTC(isoYear, 0, 4));
  const jan4IsoDow = jan4.getUTCDay() === 0 ? 7 : jan4.getUTCDay();
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - (jan4IsoDow - 1));
  week1Monday.setUTCDate(week1Monday.getUTCDate() + (weekNum - 1) * 7);
  return week1Monday;
}

/**
 * Compare two `YYYY-W##` ISO-week strings. Returns <0 if a is earlier, 0 if
 * equal, >0 if a is later. Lexical comparison is correct because the format is
 * zero-padded and ISO-year-prefixed.
 */
export function compareIsoWeeks(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}
